import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { SCORE } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate, fmtDateTime } from "@/lib/format";
import {
  Gauge, Plus, Send, Lock, XCircle, CheckCircle2, AlertTriangle, ShieldAlert,
  Pencil, Search, TrendingUp, TrendingDown, Minus, Info, Sparkles, FlaskConical, FileWarning, FileText, Bot, Check,
  Link2, X, Sprout,
} from "lucide-react";
import { toast } from "sonner";
import ScoreReport from "@/components/ScoreReport";
import { ProvenanceList, SourceRecordPicker } from "@/components/ProvenancePanel";

const BAND_STYLE = {
  advanced: "bg-forest-mid text-white border-forest-dark",
  strong: "bg-emerald-100 text-emerald-900 border-emerald-300",
  developing: "bg-amber-100 text-amber-900 border-amber-300",
  early_progress: "bg-orange-100 text-orange-900 border-orange-300",
  priority_improvement: "bg-rose-100 text-rose-900 border-rose-300",
  restricted: "bg-rose-600 text-white border-rose-800",
};
const STATUS_CHIP = {
  scored: "bg-emerald-50 text-emerald-800 border-emerald-200",
  missing: "bg-amber-50 text-amber-900 border-amber-300",
  unknown: "bg-slate-100 text-slate-700 border-slate-300",
  excluded: "bg-slate-100 text-slate-500 border-slate-200",
};
const GATE_LABEL = {
  ok: "Gates clear", unissued_estimate: "Unissued estimate", restricted: "Restricted",
};
const APPLICABILITY = [
  { v: "applicable", l: "Applicable" },
  { v: "not_applicable", l: "Not applicable" },
  { v: "unknown", l: "Unknown" },
];
const MULTIPLIERS = [{ v: 1.0, l: "1.00 — standard" }, { v: 1.25, l: "1.25 — elevated" }, { v: 1.5, l: "1.50 — high" }];
const QUALITY = [{ v: 100, l: "100 — verified" }, { v: 80, l: "80 — documented" }, { v: 60, l: "60 — indicative" }, { v: 0, l: "0 — none" }];

const fmt1 = (v) => (v == null ? "—" : Number(v).toFixed(1));
const fmt2 = (v) => (v == null ? "—" : Number(v).toFixed(2));

function Chip({ children, className = "", ...props }) {
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${className}`} {...props}>{children}</span>;
}

export default function ScoreWorkspace() {
  const { hasRole } = useAuth();
  const ws = useWorkspace();
  const navigate = useNavigate();
  const contractId = ws.contractId;
  const canPrepareRole = hasRole("tenant_admin", "contract_manager");
  const canInternal = hasRole("tenant_admin", "contract_manager", "supervisor", "finance_reviewer");
  const canApproveDisclosure = hasRole("client_rep", "tenant_admin");

  const [periods, setPeriods] = useState([]);
  const [selId, setSelId] = useState("");
  const [data, setData] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [pf, setPf] = useState({ label: "", period_start: "", period_end: "", cutoff_date: "" });
  const [entry, setEntry] = useState(null); // {kpi, form, links}
  const [drill, setDrill] = useState(null); // kpi result
  const [prov, setProv] = useState(null); // provenance for drill/entry
  const [picker, setPicker] = useState(false);
  const [critOpen, setCritOpen] = useState(false);
  const [crit, setCrit] = useState({ type: "", description: "" });
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [survivalProp, setSurvivalProp] = useState(null);
  const [sp, setSp] = useSearchParams();
  const jumpPeriod = sp.get("period");
  const jumpKpi = sp.get("kpi");
  const jumpedRef = useRef(false);

  const loadPeriods = useCallback(async () => {
    if (!contractId) return;
    const r = await api.get(`/score/periods?contract_id=${contractId}`);
    setPeriods(r.data);
    setSelId((cur) => (cur && r.data.some((p) => p.id === cur) ? cur : (r.data[0]?.id || "")));
  }, [contractId]);

  const loadPeriod = useCallback(async (id) => {
    if (!id) { setData(null); return; }
    try {
      const r = await api.get(`/score/periods/${id}`);
      setData(r.data);
    } catch (e) { toast.error(apiErr(e)); }
  }, []);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);
  useEffect(() => { loadPeriod(selId); }, [selId, loadPeriod]);

  const run = data?.run;
  const period = data?.period;
  const editable = data?.can_prepare && (period?.status === "draft" || period?.status === "rejected");

  const createPeriod = async () => {
    try {
      const { data: d } = await api.post("/score/periods", { contract_id: contractId, ...pf });
      toast.success("Assessment period created");
      setNewOpen(false); setPf({ label: "", period_start: "", period_end: "", cutoff_date: "" });
      await loadPeriods(); setSelId(d.id);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openEntry = async (k) => {
    let links = [];
    try {
      const r = await api.get(`/score/periods/${selId}/entries/${k.code}/provenance`);
      links = (r.data.links || []).filter((l) => !l.missing).map((l) => ({
        record_type: l.record_type, record_id: l.record_id, label: l.label,
        secondary: l.secondary, group: l.group, is_master: l.is_master,
      }));
    } catch { /* no links yet */ }
    setEntry({
      kpi: k,
      links,
      form: {
        raw_value: k.raw_value ?? "", passed: k.passed ?? "", required: k.required ?? "",
        applicability: k.applicability === "pending" ? "applicable" : k.applicability,
        na_predicate: k.na_predicate ?? "", context_multiplier: k.context_multiplier ?? 1.0,
        multiplier_reason: k.multiplier_reason ?? "", evidence_quality: k.evidence_quality ?? 100,
        evidence_label: k.evidence_ref?.label ?? "", notes: k.notes ?? "",
      },
    });
  };

  const loadProv = useCallback(async (code) => {
    setProv(null);
    try { const r = await api.get(`/score/periods/${selId}/entries/${code}/provenance`); setProv(r.data); }
    catch { setProv(null); }
  }, [selId]);

  const openDrill = (k) => { setDrill(k); loadProv(k.code); };

  const openRecord = (l) => {
    const lk = l.link || {};
    if (lk.area === "setup") navigate(`/setup?tab=${lk.tab}&record=${lk.record_id}`);
    else if (lk.area === "survival") navigate("/survival");
    else navigate(`/project?tab=${lk.tab || "work-orders"}`);
  };

  // Jump-to-KPI deep link (from Standards drill-down): ?period=&kpi=
  useEffect(() => {
    if (jumpPeriod && periods.some((p) => p.id === jumpPeriod) && selId !== jumpPeriod) {
      setSelId(jumpPeriod);
    }
  }, [jumpPeriod, periods]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!jumpKpi || jumpedRef.current) return;
    if (data?.period?.id === jumpPeriod && data?.run?.kpi_results) {
      const k = data.run.kpi_results.find((r) => r.code === jumpKpi);
      if (k) {
        jumpedRef.current = true;
        openDrill(k);
        const next = new URLSearchParams(sp);
        next.delete("period"); next.delete("kpi");
        setSp(next, { replace: true });
      }
    }
  }, [data, jumpKpi, jumpPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveEntry = async () => {
    const k = entry.kpi; const f = entry.form;
    const body = {
      kpi_code: k.code, applicability: f.applicability,
      na_predicate: f.applicability === "not_applicable" ? f.na_predicate : null,
      context_multiplier: Number(f.context_multiplier) || 1.0,
      multiplier_reason: Number(f.context_multiplier) > 1 ? f.multiplier_reason : null,
      evidence_quality: Number(f.evidence_quality),
      evidence_ref: f.evidence_label ? { type: "observation", label: f.evidence_label } : null,
      notes: f.notes || null,
    };
    if (f.applicability === "applicable") {
      if (k.transform === "proportion") { body.passed = f.passed === "" ? null : Number(f.passed); body.required = f.required === "" ? null : Number(f.required); }
      else { body.raw_value = f.raw_value === "" ? null : Number(f.raw_value); }
    }
    try {
      const r = await api.put(`/score/periods/${selId}/entries`, body);
      await api.put(`/score/periods/${selId}/entries/${k.code}/source-links`, {
        links: (entry.links || []).map((l) => ({ record_type: l.record_type, record_id: l.record_id })),
      });
      setData(r.data); setEntry(null);
      toast.success(`${k.code} captured`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const approve = async (code) => {
    try { const r = await api.post(`/score/periods/${selId}/entries/${code}/approve`); setData(r.data); toast.success(`${code} source result approved`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const act = async (path, okMsg, body) => {
    try { const r = await api.post(`/score/periods/${selId}/${path}`, body); setData(r.data); loadPeriods(); toast.success(okMsg); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remediate = async (id) => {
    try { const r = await api.post(`/score/critical/${id}/remediate`); setData(r.data); loadPeriods(); toast.success("Critical issue marked remediated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const [aiBusy, setAiBusy] = useState(false);
  const aiGenerate = async () => {
    setAiBusy(true);
    try { await api.post(`/score/periods/${selId}/ai-assist`); toast.success("AI insights drafted — please review"); await loadPeriod(selId); }
    catch (e) { toast.error(apiErr(e)); }
    setAiBusy(false);
  };
  const aiAccept = async () => {
    try { await api.post(`/score/periods/${selId}/ai-assist/accept`); toast.success("AI insights accepted after review"); await loadPeriod(selId); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const aiDiscard = async () => {
    try { await api.post(`/score/periods/${selId}/ai-assist/discard`); toast.success("AI draft discarded"); await loadPeriod(selId); }
    catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    if (!contractId) { setSurvivalProp(null); return; }
    api.get(`/survival/e01-proposal?contract_id=${contractId}`).then((r) => setSurvivalProp(r.data)).catch(() => setSurvivalProp(null));
  }, [contractId]);

  const applySurvival = async () => {
    try {
      const r = await api.post("/survival/e01-proposal/apply", { period_id: selId });
      toast.success(`Plant survival ${r.data.proposed_pct}% applied to E01`);
      await loadPeriod(selId); loadPeriods();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const whatIf = useMemo(() => {
    if (!run) return [];
    return run.kpi_results
      .filter((k) => k.factor === 1 && (k.status === "scored" || k.status === "missing" || k.status === "unknown"))
      .map((k) => ({ ...k, gain: k.effective_weight * (100 - (k.kpi_score || 0)) / 100 }))
      .filter((k) => k.gain > 0.05)
      .sort((a, b) => (a.status !== "scored" && b.status === "scored" ? -1 : b.gain - a.gain))
      .slice(0, 5);
  }, [run]);

  if (!contractId) return <p className="text-sm text-muted-foreground">Select a contract to open its ROOTS Score.</p>;

  return (
    <div className="space-y-5" data-testid={SCORE.root}>
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-forest-dark"><Gauge className="h-5 w-5 text-forest-mid" /> ROOTS Performance Score</h2>
          <p className="text-sm text-muted-foreground">Deterministic, auditable 0–100 score · SG Maintenance v1 profile</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {periods.length > 0 && (
            <Select value={selId} onValueChange={setSelId}>
              <SelectTrigger className="h-9 w-[150px]" data-testid={SCORE.periodSelect}><SelectValue placeholder="Period" /></SelectTrigger>
              <SelectContent>
                {periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.label} · {p.status}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {period?.status === "issued" && (
            <Button size="sm" variant="outline" onClick={() => setReportOpen(true)} data-testid={SCORE.reportBtn}><FileText className="mr-1 h-4 w-4" /> Report</Button>
          )}
          {canPrepareRole && <Button size="sm" onClick={() => setNewOpen(true)} data-testid={SCORE.newPeriodBtn} className="bg-forest-mid hover:bg-forest-dark"><Plus className="mr-1 h-4 w-4" /> New period</Button>}
        </div>
      </div>

      {!period ? (
        <Card className="border-dashed border-sage-light p-10 text-center text-sm text-muted-foreground">
          No assessment periods yet. {canPrepareRole ? "Create a quarterly period to begin capturing KPI values." : "Ask a contract manager to start one."}
        </Card>
      ) : (
        <>
          {/* draft / issued banner */}
          {period.status === "issued" ? (
            <div className="flex items-start gap-2 rounded-lg border-l-4 border-forest-mid bg-sage-surface p-3" data-testid="score-issued-banner">
              <Lock className="mt-0.5 h-4 w-4 text-forest-mid" />
              <p className="text-xs text-forest-dark"><b>Issued & frozen.</b> {period.issued_by_name ? `Issued by ${period.issued_by_name} · ` : ""}{fmtDateTime(period.issued_at)}. Inputs are immutable; a correction creates a new run. Issued is not the same as published or externally assured.</p>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-lg border-l-4 border-amber-400 bg-amber-50/80 p-3">
              <FileWarning className="mt-0.5 h-4 w-4 text-amber-600" />
              <p className="text-xs text-amber-900"><b>Draft — {period.status.replace("_", " ")}.</b> Numbers refresh live as inputs change and are not a published result.{period.reject_reason ? ` Returned by reviewer: "${period.reject_reason}"` : ""}</p>
            </div>
          )}

          {/* summary + pillars */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
            <Card className="border-sage-light p-5 lg:col-span-1" data-testid={SCORE.overallTile}>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{period.label} · Overall</p>
              <div className="mt-1 flex items-end gap-2">
                {run.overall_display != null ? (
                  <span className="font-heading text-5xl font-bold text-forest-dark">{fmt1(run.overall_display)}</span>
                ) : run.range ? (
                  <span className="font-heading text-3xl font-bold text-forest-dark">{fmt1(run.range.lower)}–{fmt1(run.range.upper)}</span>
                ) : (
                  <span className="font-heading text-3xl font-bold text-muted-foreground">—</span>
                )}
                <span className="mb-1 text-sm text-muted-foreground">/ 100</span>
              </div>
              <div className="mt-2">
                <Chip className={BAND_STYLE[run.display_band_key] || "bg-slate-100 text-slate-700 border-slate-200"} data-testid={SCORE.bandChip}>
                  {run.gate === "restricted" && <ShieldAlert className="h-3.5 w-3.5" />}
                  {run.display_band_label || "Not scored"}
                </Chip>
                {run.band_capped && <span className="ml-2 text-[11px] text-amber-700">capped by weak pillar</span>}
              </div>
              {data.prior && (
                <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                  vs {data.prior.label}: {data.prior.delta == null ? "—" : (
                    <span className={`inline-flex items-center gap-0.5 font-semibold ${data.prior.delta > 0 ? "text-emerald-700" : data.prior.delta < 0 ? "text-rose-600" : "text-slate-500"}`}>
                      {data.prior.delta > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : data.prior.delta < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                      {data.prior.delta > 0 ? "+" : ""}{fmt1(data.prior.delta)}
                    </span>
                  )}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5">
                <Chip className="bg-white text-forest-dark border-sage-light">Coverage C {fmt1(run.coverage_display)}%</Chip>
                <Chip className="bg-white text-forest-dark border-sage-light">Evidence Q {run.evidence_index == null ? "—" : fmt1(run.evidence_index)}</Chip>
                <Chip className={run.gate === "ok" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : run.gate === "restricted" ? "bg-rose-100 text-rose-900 border-rose-300" : "bg-amber-50 text-amber-900 border-amber-300"}>{GATE_LABEL[run.gate]}</Chip>
              </div>
              {run.reason && <p className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground"><Info className="h-3 w-3" /> {run.reason.replace(/_/g, " ").toLowerCase()}</p>}
            </Card>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:col-span-3">
              {["environmental", "social", "economic"].map((p) => {
                const ps = run.pillars[p];
                return (
                  <Card key={p} className="border-sage-light p-4" data-testid={SCORE.pillarTile(p)}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{data.profile.pillar_labels[p]} · {ps.budget}%</p>
                    {ps.score != null ? (
                      <p className="mt-1 font-heading text-3xl font-bold text-forest-dark">{fmt1(ps.display)}</p>
                    ) : (
                      <p className="mt-1 font-heading text-2xl font-bold text-muted-foreground">{fmt1(ps.lower)}–{fmt1(ps.upper)}</p>
                    )}
                    <div className="mt-2 h-2 rounded-full bg-slate-100">
                      <div className="h-2 rounded-full bg-forest-mid" style={{ width: `${Math.min(100, ps.score ?? ps.lower ?? 0)}%` }} />
                    </div>
                    <p className="mt-1.5 text-[11px] text-muted-foreground">Contribution {fmt2(ps.contribution)} / {ps.budget} pts {ps.score != null && ps.score < 50 ? "· below floor" : ""}</p>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* workflow actions */}
          <Card className="flex flex-wrap items-center justify-between gap-3 border-sage-light p-4">
            <div className="text-xs text-muted-foreground">
              <span className="font-semibold text-forest-dark">Workflow:</span> {period.status.replace("_", " ")} · prepared by {period.preparer_name || "—"} · window {fmtDate(period.period_start)}–{fmtDate(period.period_end)} {data.window_complete ? "(complete)" : "(open)"}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {data.can_review && period.status !== "issued" && (
                <Button size="sm" variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setCritOpen(true)} data-testid={SCORE.criticalBtn}><ShieldAlert className="mr-1 h-4 w-4" /> Raise critical</Button>
              )}
              {editable && (
                <Button size="sm" onClick={() => act("submit", "Submitted for review")} data-testid={SCORE.submitBtn} className="bg-forest-mid hover:bg-forest-dark"><Send className="mr-1 h-4 w-4" /> Submit for review</Button>
              )}
              {period.status === "in_review" && data.can_review && (
                <Button size="sm" variant="outline" onClick={() => setRejectOpen(true)} data-testid={SCORE.rejectBtn}><XCircle className="mr-1 h-4 w-4" /> Reject</Button>
              )}
              {period.status === "in_review" && data.can_issue && (
                <Button size="sm" disabled={!run.eligible_to_issue || data.is_preparer} onClick={() => act("issue", "Score issued & frozen")} data-testid={SCORE.issueBtn} className="bg-forest-mid hover:bg-forest-dark" title={data.is_preparer ? "Separation of duty: the preparer cannot issue" : (run.issue_blocks || []).join("; ")}>
                  <Lock className="mr-1 h-4 w-4" /> Issue score
                </Button>
              )}
            </div>
          </Card>

          {/* Plant Survival Register → E01 proposal */}
          {editable && survivalProp?.available && (
            <Card className="flex flex-wrap items-center justify-between gap-3 border-emerald-200 bg-emerald-50/50 p-4" data-testid={SCORE.survivalCard}>
              <div className="flex items-start gap-2">
                <Sprout className="mt-0.5 h-4 w-4 shrink-0 text-forest-mid" />
                <p className="text-xs text-forest-dark">
                  <b>Plant Survival Register</b> proposes <b>E01 = {survivalProp.proposed_pct}%</b> ({survivalProp.tier}) — count-weighted across {survivalProp.register_ids.length} zone{survivalProp.register_ids.length === 1 ? "" : "s"}{survivalProp.as_of ? `, latest audit ${survivalProp.as_of}` : ""}. Applying backs E01 with the audited counts.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => navigate("/survival")}>Open register</Button>
                <Button size="sm" className="h-8 bg-forest-mid text-xs hover:bg-forest-dark" onClick={applySurvival} data-testid={SCORE.survivalApply}>
                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Apply to E01
                </Button>
              </div>
            </Card>
          )}

          {/* issue blockers */}
          {period.status !== "issued" && (run.issue_blocks || []).length > 0 && (
            <Card className="border-amber-200 bg-amber-50/60 p-3">
              <p className="flex items-center gap-1 text-xs font-semibold text-amber-900"><AlertTriangle className="h-3.5 w-3.5" /> Before this score can be issued:</p>
              <ul className="mt-1 list-inside list-disc text-[11px] text-amber-900">{run.issue_blocks.map((b, i) => <li key={i}>{b}</li>)}</ul>
            </Card>
          )}

          {/* open criticals */}
          {(data.criticals || []).length > 0 && (
            <Card className="border-rose-200 bg-rose-50/50 p-4">
              <p className="flex items-center gap-1 text-sm font-semibold text-rose-800"><ShieldAlert className="h-4 w-4" /> Critical issues</p>
              <div className="mt-2 space-y-2">
                {data.criticals.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-2 rounded-lg border border-rose-200 bg-white p-2 text-xs">
                    <span className="text-forest-dark"><b className="capitalize">{c.type}</b> — {c.description} <span className="text-muted-foreground">({c.status}, {c.raised_by_name})</span></span>
                    {c.status !== "remediated" && data.can_issue && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => remediate(c.id)} data-testid={SCORE.remediateBtn(c.id)}>Mark remediated</Button>}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* AI insights (issued, internal only) */}
          {period.status === "issued" && canInternal && (
            <Card className="border-sage-light p-4" data-testid={SCORE.aiCard}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-1.5 font-heading text-base font-semibold text-forest-dark"><Bot className="h-4 w-4 text-indigo-500" /> AI insights</p>
                <span className="inline-flex items-center gap-1 rounded bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-800"><FlaskConical className="h-3 w-3" /> AI-drafted · human review required · never the source of any score</span>
              </div>
              {!period.ai_assist ? (
                <div className="mt-3 flex items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">Draft a plain-language explanation of this score and prioritised recommendations, grounded only in the issued figures.</p>
                  <Button size="sm" disabled={aiBusy} onClick={aiGenerate} data-testid={SCORE.aiGenerate} className="shrink-0 bg-indigo-600 hover:bg-indigo-700">
                    <Sparkles className="mr-1 h-4 w-4" /> {aiBusy ? "Drafting…" : "Draft with AI"}
                  </Button>
                </div>
              ) : (
                <div className="mt-3 space-y-3">
                  <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-forest-dark">{period.ai_assist.draft}</p>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
                    <span>
                      {period.ai_assist.source === "fallback" ? "Automatic summary (AI unavailable)" : `Drafted by ${period.ai_assist.model || "AI"}`}
                      {period.ai_assist.generated_by_name ? ` · requested by ${period.ai_assist.generated_by_name}` : ""}
                      {period.ai_assist.accepted && <span className="ml-1 inline-flex items-center gap-0.5 font-semibold text-emerald-700"><Check className="h-3 w-3" /> accepted by {period.ai_assist.accepted_by_name}</span>}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={aiBusy} onClick={aiGenerate} data-testid="score-ai-regenerate"><Sparkles className="mr-1 h-3 w-3" /> Regenerate</Button>
                      {data.can_review && !period.ai_assist.accepted && (
                        <Button size="sm" className="h-7 bg-emerald-600 text-xs hover:bg-emerald-700" onClick={aiAccept} data-testid={SCORE.aiAccept}><Check className="mr-1 h-3 w-3" /> Accept after review</Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600" onClick={aiDiscard} data-testid={SCORE.aiDiscard}>Discard</Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* contribution table */}
          <Card className="border-sage-light p-0">
            <div className="flex items-center justify-between border-b border-sage-light p-4">
              <h3 className="font-heading text-base font-semibold text-forest-dark">Contribution table <span className="text-xs font-normal text-muted-foreground">— weighted points per KPI</span></h3>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>KPI</TableHead><TableHead>Raw</TableHead><TableHead>Score</TableHead>
                    <TableHead>Eff. weight</TableHead><TableHead>Contribution</TableHead>
                    <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {run.kpi_results.map((k) => (
                    <TableRow key={k.code} data-testid={SCORE.contribRow(k.code)} className={k.pillar === "environmental" ? "" : ""}>
                      <TableCell className="text-sm">
                        <span className="font-mono text-xs text-muted-foreground">{k.code}</span> {k.name}
                        {k.mandatory && <span className="ml-1 rounded bg-rose-100 px-1 text-[9px] font-semibold text-rose-700">MANDATORY</span>}
                        {k.code === "E01" && (
                          <button onClick={() => navigate("/survival")} data-testid={SCORE.survivalLink}
                            className="ml-2 inline-flex items-center gap-1 align-middle text-xs font-medium text-forest-mid hover:text-forest-dark hover:underline">
                            <Sprout className="h-3 w-3" /> Plant Survival Register
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{k.transform === "proportion" ? (k.passed != null ? `${k.passed}/${k.required}` : "—") : (k.raw_value != null ? `${k.raw_value} ${k.unit}` : "—")}</TableCell>
                      <TableCell className="text-sm font-semibold">{k.kpi_score != null ? fmt1(k.kpi_score) : "—"}</TableCell>
                      <TableCell className="text-sm">{fmt2(k.effective_weight)}</TableCell>
                      <TableCell className="text-sm font-semibold text-forest-dark">{k.status === "scored" ? fmt2(k.contribution) : "—"}</TableCell>
                      <TableCell><Chip className={STATUS_CHIP[k.status] || "bg-slate-100 text-slate-700 border-slate-200"}>{k.status}</Chip></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openDrill(k)} data-testid={SCORE.drillBtn(k.code)} title="Drill down"><Search className="h-3.5 w-3.5" /></Button>
                          {editable && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEntry(k)} data-testid={SCORE.editBtn(k.code)} title="Edit value"><Pencil className="h-3.5 w-3.5" /></Button>}
                          {data.can_review && period.status !== "issued" && (k.applicability !== "pending") && !k.reviewer_approved && (
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-700" onClick={() => approve(k.code)} data-testid={SCORE.approveBtn(k.code)} title="Approve source result"><CheckCircle2 className="h-3.5 w-3.5" /></Button>
                          )}
                          {k.reviewer_approved && <CheckCircle2 className="h-4 w-4 text-emerald-600" title={`Approved${k.approved_by_name ? " by " + k.approved_by_name : ""}`} />}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* improvement actions (what-if) */}
          {whatIf.length > 0 && period.status !== "issued" && (
            <Card className="border-sage-light p-4">
              <p className="flex items-center gap-1.5 font-heading text-base font-semibold text-forest-dark"><Sparkles className="h-4 w-4 text-forest-mid" /> Improvement actions</p>
              <div className="mt-1 inline-flex items-center gap-1 rounded bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-800"><FlaskConical className="h-3 w-3" /> What-if scenario — estimates only, never a published or issued score</div>
              <div className="mt-3 space-y-2">
                {whatIf.map((k) => (
                  <div key={k.code} className="flex items-center justify-between gap-2 rounded-lg border border-sage-light bg-sage-surface p-2.5 text-sm">
                    <span className="text-forest-dark"><span className="font-mono text-xs text-muted-foreground">{k.code}</span> {k.name} <span className="text-xs text-muted-foreground">({k.status === "scored" ? `at ${fmt1(k.kpi_score)}` : k.status})</span></span>
                    <span className="shrink-0 font-semibold text-forest-mid">+{fmt2(k.gain)} pts possible</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* new period dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid={SCORE.periodDialog}>
          <DialogHeader><DialogTitle className="font-heading">New quarterly assessment period</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Label</Label><Input value={pf.label} onChange={(e) => setPf({ ...pf, label: e.target.value })} placeholder="2026 Q2" data-testid={SCORE.field("label")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Period start</Label><Input type="date" value={pf.period_start} onChange={(e) => setPf({ ...pf, period_start: e.target.value })} data-testid={SCORE.field("start")} /></div>
              <div className="space-y-1.5"><Label>Period end</Label><Input type="date" value={pf.period_end} onChange={(e) => setPf({ ...pf, period_end: e.target.value })} data-testid={SCORE.field("end")} /></div>
            </div>
            <div className="space-y-1.5"><Label>Cut-off date</Label><Input type="date" value={pf.cutoff_date} onChange={(e) => setPf({ ...pf, cutoff_date: e.target.value })} data-testid={SCORE.field("cutoff")} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button><Button onClick={createPeriod} disabled={!pf.label || !pf.period_start || !pf.period_end} className="bg-forest-mid hover:bg-forest-dark" data-testid={SCORE.periodSaveBtn}>Create period</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* entry edit dialog */}
      <Dialog open={!!entry} onOpenChange={(v) => !v && setEntry(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid={SCORE.entryDialog}>
          {entry && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{entry.kpi.code} · {entry.kpi.name}</DialogTitle></DialogHeader>
              <p className="text-xs text-muted-foreground">{entry.kpi.pillar_label} · base weight {entry.kpi.base_weight} · {entry.kpi.transform === "proportion" ? "proportion (passed ÷ required)" : `${entry.kpi.transform === "higher" ? "higher is better" : "lower is better"}, anchors ${entry.kpi.anchor0}→0 / ${entry.kpi.anchor100}→100`}</p>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Applicability</Label>
                  <Select value={entry.form.applicability} onValueChange={(v) => setEntry((s) => ({ ...s, form: { ...s.form, applicability: v } }))}>
                    <SelectTrigger data-testid={SCORE.field("applicability")}><SelectValue /></SelectTrigger>
                    <SelectContent>{APPLICABILITY.filter((a) => !(a.v === "not_applicable" && entry.kpi.mandatory)).map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
                  </Select>
                  {entry.kpi.mandatory && <p className="text-[11px] text-rose-700">Mandatory KPI — cannot be marked Not applicable.</p>}
                </div>

                {entry.form.applicability === "applicable" && (
                  entry.kpi.transform === "proportion" ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Passed</Label><Input type="number" value={entry.form.passed} onChange={(e) => setEntry((s) => ({ ...s, form: { ...s.form, passed: e.target.value } }))} data-testid={SCORE.field("passed")} /></div>
                      <div className="space-y-1.5"><Label>Required</Label><Input type="number" value={entry.form.required} onChange={(e) => setEntry((s) => ({ ...s, form: { ...s.form, required: e.target.value } }))} data-testid={SCORE.field("required")} /></div>
                    </div>
                  ) : (
                    <div className="space-y-1.5"><Label>Raw value ({entry.kpi.unit})</Label><Input type="number" value={entry.form.raw_value} onChange={(e) => setEntry((s) => ({ ...s, form: { ...s.form, raw_value: e.target.value } }))} data-testid={SCORE.field("raw")} /></div>
                  )
                )}

                {entry.form.applicability === "not_applicable" && (
                  <div className="space-y-1.5"><Label>Not-applicable predicate (required)</Label><Textarea rows={2} value={entry.form.na_predicate} onChange={(e) => setEntry((s) => ({ ...s, form: { ...s.form, na_predicate: e.target.value } }))} placeholder="Why this KPI does not apply to this contract" data-testid={SCORE.field("predicate")} /><p className="text-[11px] text-muted-foreground">Takes effect only after a reviewer approves the exclusion.</p></div>
                )}
                {entry.form.applicability === "unknown" && (
                  <p className="rounded-lg bg-slate-50 p-2 text-[11px] text-muted-foreground">Unknown opens a profiling task and withholds the final score. Weights are never silently redistributed.</p>
                )}

                {entry.form.applicability === "applicable" && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Context multiplier</Label>
                        <Select value={String(entry.form.context_multiplier)} onValueChange={(v) => setEntry((s) => ({ ...s, form: { ...s.form, context_multiplier: Number(v) } }))}>
                          <SelectTrigger data-testid={SCORE.field("multiplier")}><SelectValue /></SelectTrigger>
                          <SelectContent>{MULTIPLIERS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5"><Label>Evidence quality</Label>
                        <Select value={String(entry.form.evidence_quality)} onValueChange={(v) => setEntry((s) => ({ ...s, form: { ...s.form, evidence_quality: Number(v) } }))}>
                          <SelectTrigger data-testid={SCORE.field("quality")}><SelectValue /></SelectTrigger>
                          <SelectContent>{QUALITY.map((q) => <SelectItem key={q.v} value={String(q.v)}>{q.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    {Number(entry.form.context_multiplier) > 1 && (
                      <div className="space-y-1.5"><Label>Multiplier reason</Label><Input value={entry.form.multiplier_reason} onChange={(e) => setEntry((s) => ({ ...s, form: { ...s.form, multiplier_reason: e.target.value } }))} placeholder="Named triggering reason" data-testid={SCORE.field("mult-reason")} /></div>
                    )}
                    <div className="space-y-1.5"><Label>Evidence link</Label><Input value={entry.form.evidence_label} onChange={(e) => setEntry((s) => ({ ...s, form: { ...s.form, evidence_label: e.target.value } }))} placeholder="Reference to an existing record" data-testid={SCORE.field("evidence")} /></div>
                  </>
                )}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>Source records <span className="font-normal text-muted-foreground">— where this number comes from</span></Label>
                    <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPicker(true)} data-testid={SCORE.sourcePickerBtn}><Link2 className="mr-1 h-3.5 w-3.5" /> Attach records</Button>
                  </div>
                  {entry.links.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground">No records linked yet. Attach the data-management or operational records this value rests on.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {entry.links.map((l) => (
                        <span key={l.record_type + l.record_id} className="inline-flex items-center gap-1 rounded-full border border-sage-light bg-sage-surface px-2 py-0.5 text-[11px] text-forest-dark" data-testid={SCORE.sourceChip(l.record_type, l.record_id)}>
                          {l.label}
                          <button type="button" onClick={() => setEntry((s) => ({ ...s, links: s.links.filter((x) => !(x.record_type === l.record_type && x.record_id === l.record_id)) }))} className="text-muted-foreground hover:text-rose-600"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={entry.form.notes} onChange={(e) => setEntry((s) => ({ ...s, form: { ...s.form, notes: e.target.value } }))} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setEntry(null)}>Cancel</Button><Button onClick={saveEntry} className="bg-forest-mid hover:bg-forest-dark" data-testid={SCORE.entrySaveBtn}>Save value</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {entry && (
        <SourceRecordPicker open={picker} onOpenChange={setPicker} contractId={contractId}
          selected={entry.links} onChange={(links) => setEntry((s) => ({ ...s, links }))} />
      )}

      {/* drill-down modal */}
      <Dialog open={!!drill} onOpenChange={(v) => { if (!v) { setDrill(null); setProv(null); } }}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid={SCORE.drillModal}>
          {drill && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{drill.code} · {drill.name}</DialogTitle></DialogHeader>
              {drill.code === "E01" && (
                <button onClick={() => navigate("/survival")} data-testid={SCORE.survivalLink + "-drill"}
                  className="inline-flex w-fit items-center gap-1.5 rounded-md border border-sage-light bg-sage-surface/60 px-2.5 py-1.5 text-xs font-medium text-forest-dark hover:bg-sage-light">
                  <Sprout className="h-3.5 w-3.5 text-forest-mid" /> Open Plant Survival Register →
                </button>
              )}
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2 rounded-lg bg-sage-surface p-3 text-xs">
                  <div><span className="text-muted-foreground">Pillar</span><br /><b>{drill.pillar_label}</b></div>
                  <div><span className="text-muted-foreground">Base weight</span><br /><b>{drill.base_weight}</b></div>
                  <div><span className="text-muted-foreground">Direction</span><br /><b>{drill.transform === "proportion" ? "Proportion" : drill.transform === "higher" ? "Higher is better" : "Lower is better"}</b></div>
                  <div><span className="text-muted-foreground">Unit</span><br /><b>{drill.unit}</b></div>
                  {drill.transform !== "proportion" && <div className="col-span-2"><span className="text-muted-foreground">Anchors</span><br /><b>{drill.anchor0} → 0 · {drill.anchor100} → 100</b></div>}
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Effective weight derivation</p>
                  <p className="mt-1 rounded bg-slate-50 p-2 font-mono text-[11px]">{drill.weight_steps}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">base × applicability × multiplier ÷ pillar active sum, then × pillar budget</p>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Score arithmetic</p>
                  {drill.status === "scored" ? (
                    <ol className="mt-1 list-inside list-decimal space-y-0.5 rounded bg-slate-50 p-2 font-mono text-[11px]">
                      {drill.score_steps.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                  ) : drill.status === "excluded" ? (
                    <p className="mt-1 rounded bg-slate-50 p-2 text-[11px]">Excluded (Not applicable, approved): {drill.exclusion_reason}</p>
                  ) : (
                    <p className="mt-1 rounded bg-amber-50 p-2 text-[11px] text-amber-900">{drill.status === "unknown" ? "Applicability unknown — final score withheld." : `Not calculable: ${drill.error_reason}`}</p>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg border border-sage-light p-2"><p className="text-[10px] text-muted-foreground">KPI score</p><p className="font-heading text-lg font-bold text-forest-dark">{drill.kpi_score != null ? fmt1(drill.kpi_score) : "—"}</p></div>
                  <div className="rounded-lg border border-sage-light p-2"><p className="text-[10px] text-muted-foreground">Eff. weight</p><p className="font-heading text-lg font-bold text-forest-dark">{fmt2(drill.effective_weight)}</p></div>
                  <div className="rounded-lg border border-sage-light p-2"><p className="text-[10px] text-muted-foreground">Contribution</p><p className="font-heading text-lg font-bold text-forest-dark">{drill.status === "scored" ? fmt2(drill.contribution) : "—"}</p></div>
                </div>

                <div className="text-xs text-muted-foreground">
                  <p><b>Applicability:</b> {drill.applicability}{drill.na_predicate ? ` — ${drill.na_predicate}` : ""}</p>
                  <p><b>Context multiplier:</b> {drill.context_multiplier}{drill.multiplier_reason ? ` (${drill.multiplier_reason})` : ""}</p>
                  <p><b>Evidence quality:</b> {drill.evidence_quality}{drill.evidence_ref?.label ? ` · ${drill.evidence_ref.label}` : ""}</p>
                  <p><b>Source approved:</b> {drill.reviewer_approved ? `Yes${drill.approved_by_name ? " — " + drill.approved_by_name : ""}` : "No"}</p>
                  {(drill.flags || []).length > 0 && <p className="mt-1 text-amber-700">{drill.flags.join(" · ")}</p>}
                </div>

                <div className="border-t border-sage-light pt-3">
                  <ProvenanceList prov={prov} onOpenRecord={openRecord} />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* critical dialog */}
      <Dialog open={critOpen} onOpenChange={setCritOpen}>
        <DialogContent data-testid={SCORE.criticalDialog}>
          <DialogHeader><DialogTitle className="font-heading">Raise a critical issue</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">A credible serious incident, severe labour abuse, major unauthorised environmental damage, falsified evidence or a critical-control failure overrides any positive label to Restricted.</p>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Type</Label><Input value={crit.type} onChange={(e) => setCrit({ ...crit, type: e.target.value })} placeholder="e.g. safety incident" data-testid={SCORE.field("crit-type")} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea rows={3} value={crit.description} onChange={(e) => setCrit({ ...crit, description: e.target.value })} data-testid={SCORE.field("crit-desc")} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCritOpen(false)}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700" disabled={!crit.type || !crit.description} onClick={() => { act("critical", "Critical issue raised", crit); setCritOpen(false); setCrit({ type: "", description: "" }); }} data-testid="score-critical-save">Raise critical</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Reject & return to preparer</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label>Reason</Label><Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid={SCORE.field("reject-reason")} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button><Button disabled={!rejectReason} onClick={() => { act("reject", "Returned to preparer", { reason: rejectReason }); setRejectOpen(false); setRejectReason(""); }} data-testid="score-reject-save">Reject</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {reportOpen && (
        <ScoreReport
          periodId={selId}
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          canInternal={canInternal}
          canApproveDisclosure={canApproveDisclosure}
          onDisclosureChange={() => loadPeriod(selId)}
        />
      )}
    </div>
  );
}
